import ModalExcluir from '@/shared/components/modals/ModalExcluir';
import {
  CreateGastoDto,
  ReadGastoDto,
  ReadPersonSelectDto,
  ReadTipoGastoDto,
  UpdateGastoDto,
} from '@/api.types';
import { DeleteGasto, GetGasto, SetGasto } from './gasto.service';
import { GetTipoGastos } from '../tipo-gasto/tipo-gasto.service';
import { EditadoPorRodape } from '@/shared/components/EditadoPorRodape';
import { convertToGMTPlus3 } from '@/shared/utils/datas';
import { readForSelect } from '@/modules/people/person.service';
import { Modal } from 'react-bootstrap';
import { SubmitHandler, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { useEffect, useState } from 'react';

interface ModalGastoFormProps {
  show: boolean;
  onHide: () => void;
  onSaved: () => void;
  gastoId?: number;
  tipoGastoId?: number;
}

export function ModalGastoForm({
  show,
  onHide,
  onSaved,
  gastoId = 0,
  tipoGastoId = 0,
}: ModalGastoFormProps) {
  const [modaExcluirShow, setModalExcluirShow] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [tipoGastos, setTipoGastos] = useState<ReadTipoGastoDto[]>([]);
  const [pessoas, setPessoas] = useState<ReadPersonSelectDto[]>([]);

  const id = gastoId > 0 ? gastoId : 0;
  const btnSalvar = id > 0 ? 'Salvar' : 'Cadastrar';

  const { register, handleSubmit, reset, watch, setValue } = useForm<ReadGastoDto>();

  const editadoPor = watch('editadoPor');
  const editadoEm = watch('editadoEm');
  const tipoGastoIdForm = watch('tipoGasto.id');
  const tipoGastoSelecionado = tipoGastos.find((tg) => tg.id === tipoGastoIdForm);

  useEffect(() => {
    if (!show) return;

    let cancelled = false;

    Promise.all([
      GetTipoGastos(0, 1000),
      readForSelect({ skip: 0, take: 1000, isColaborador: true }),
      id > 0 ? GetGasto(id) : Promise.resolve(null),
    ])
      .then(([tiposResult, pessoasResult, gasto]) => {
        if (cancelled) return;

        setTipoGastos(tiposResult.objects);
        setPessoas(pessoasResult[0]);

        if (gasto) {
          reset({
            ...gasto,
            data: gasto.data
              ? new Date(gasto.data).toISOString().split('T')[0]
              : new Date().toISOString().split('T')[0],
          });
          return;
        }

        reset({
          data: new Date().toISOString().split('T')[0],
          tipoGasto:
            tipoGastoId > 0
              ? ({ id: tipoGastoId } as ReadGastoDto['tipoGasto'])
              : undefined,
        } as ReadGastoDto);
      })
      .catch((res) => toast.error(String(res)));

    return () => {
      cancelled = true;
    };
  }, [show, id, tipoGastoId, reset]);

  useEffect(() => {
    if (!show || id > 0 || tipoGastoId <= 0 || tipoGastos.length === 0) return;
    setValue('tipoGasto.id', tipoGastoId);
  }, [show, id, tipoGastoId, tipoGastos, setValue]);

  const onSubmit: SubmitHandler<ReadGastoDto> = (data) => {
    setIsLoading(true);

    const dataToSave: CreateGastoDto | UpdateGastoDto = {
      id: id > 0 ? id : undefined,
      data: convertToGMTPlus3(data.data).toISOString(),
      quemGastou: { id: data.quemGastou?.id || '' },
      valor: Number(data.valor) || 0,
      tipoGasto: { id: Number(data.tipoGasto?.id) },
      valorColuna1: data.valorColuna1 || undefined,
      valorColuna2: data.valorColuna2 || undefined,
      valorColuna3: data.valorColuna3 || undefined,
    };

    SetGasto(dataToSave)
      .then((res) => {
        toast.success(String(res));
        onSaved();
        onHide();
      })
      .catch((res) => {
        toast.error(String(res));
      })
      .finally(() => setIsLoading(false));
  };

  const handleExcluir = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (id <= 0) return;

    setModalExcluirShow(false);
    DeleteGasto(id)
      .then((res) => {
        toast.success(String(res));
        onSaved();
        onHide();
      })
      .catch((res) => {
        toast.error(String(res));
      });
  };

  return (
    <>
      <Modal show={show} onHide={onHide} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>{id > 0 ? 'Editar gasto' : 'Cadastrar gasto'}</Modal.Title>
        </Modal.Header>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Modal.Body>
            <div className="row g-2">
              <div className="col-md-4">
                <label htmlFor="modal-gasto-data" className="form-label">
                  Data:
                </label>
                <input
                  id="modal-gasto-data"
                  type="date"
                  className="form-control"
                  {...register('data', { required: true })}
                />
              </div>

              <div className="col-md-8">
                <label htmlFor="modal-gasto-quem" className="form-label">
                  Quem Gastou:
                </label>
                <select
                  id="modal-gasto-quem"
                  className="form-select"
                  {...register('quemGastou.id', { required: true })}
                >
                  <option value="">Selecione...</option>
                  {pessoas.map((pessoa) => (
                    <option key={pessoa.id} value={pessoa.id}>
                      {pessoa.nome}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-4">
                <label htmlFor="modal-gasto-valor" className="form-label">
                  Valor:
                </label>
                <div className="input-group">
                  <span className="input-group-text">R$</span>
                  <input
                    id="modal-gasto-valor"
                    type="number"
                    step="0.01"
                    min="0"
                    className="form-control"
                    {...register('valor', { required: true, valueAsNumber: true })}
                  />
                </div>
              </div>

              <div className="col-md-8 mb-2">
                <label htmlFor="modal-gasto-tipo" className="form-label">
                  Tipo de Gasto:
                </label>
                <select
                  id="modal-gasto-tipo"
                  className="form-select"
                  {...register('tipoGasto.id', { required: true, valueAsNumber: true })}
                >
                  <option value="">Selecione...</option>
                  {tipoGastos.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.descricao}
                    </option>
                  ))}
                </select>
              </div>

              {tipoGastoSelecionado?.coluna1 && (
                <div className="mb-2 col-md-4">
                  <label htmlFor="modal-gasto-col1" className="form-label">
                    {tipoGastoSelecionado.coluna1}:
                  </label>
                  <input
                    id="modal-gasto-col1"
                    type="text"
                    className="form-control"
                    {...register('valorColuna1')}
                  />
                </div>
              )}
              {tipoGastoSelecionado?.coluna2 && (
                <div className="mb-2 col-md-4">
                  <label htmlFor="modal-gasto-col2" className="form-label">
                    {tipoGastoSelecionado.coluna2}:
                  </label>
                  <input
                    id="modal-gasto-col2"
                    type="text"
                    className="form-control"
                    {...register('valorColuna2')}
                  />
                </div>
              )}
              {tipoGastoSelecionado?.coluna3 && (
                <div className="mb-2 col-md-4">
                  <label htmlFor="modal-gasto-col3" className="form-label">
                    {tipoGastoSelecionado.coluna3}:
                  </label>
                  <input
                    id="modal-gasto-col3"
                    type="text"
                    className="form-control"
                    {...register('valorColuna3')}
                  />
                </div>
              )}

              {id > 0 && (
                <EditadoPorRodape
                  dados={{
                    editadoPor: editadoPor ?? undefined,
                    editadoEm: editadoEm,
                  }}
                />
              )}
            </div>
          </Modal.Body>
          <Modal.Footer className="justify-content-between">
            <div>
              {id > 0 && (
                <button
                  type="button"
                  className="btn btn-outline-danger"
                  onClick={() => setModalExcluirShow(true)}
                >
                  Excluir
                </button>
              )}
            </div>
            <div className="d-flex gap-2">
              <button type="button" className="btn btn-secondary" onClick={onHide}>
                Cancelar
              </button>
              <button type="submit" className="btn btn-success" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden />
                    {btnSalvar === 'Cadastrar' ? 'Cadastrando...' : 'Salvando...'}
                  </>
                ) : (
                  btnSalvar
                )}
              </button>
            </div>
          </Modal.Footer>
        </form>
      </Modal>

      <ModalExcluir
        show={modaExcluirShow}
        handleExcluir={handleExcluir}
        handleHide={() => setModalExcluirShow(false)}
      />
    </>
  );
}
